// ============================================================================
// AdminResourcesPanel — manage bookable resources (rooms + staff). Task #3.
// ============================================================================
// Sidebar entry: #admin/resources. UIF only.
//
// Staff resources let you assign team members to events with conflict
// detection. Rooms appear here too for capacity / description edits, but
// the seed in migration 010 already covers them so day-to-day usage is
// adding/removing staff.
//
// Form is a single inline row that toggles between "add new" and "edit
// existing". Active + archived are both shown so a deactivated staff member
// can be re-activated.
//
// Hourly rate is captured here as future-proofing for time-tracking (#53)
// and grant billing — not used by the booking flow today.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Save, X, Edit2, Archive, RotateCcw, AlertCircle } from 'lucide-react';
import { api, type AdminResource, type ResourceInput } from '../api';

interface Props {
  token: string;
}

type FormMode =
  | { kind: 'closed' }
  | { kind: 'new' }
  | { kind: 'edit'; resource: AdminResource };

const emptyForm: ResourceInput = {
  resource_name: '',
  resource_type: 'staff',
  capacity: null,
  description: null,
  staff_user_id: null,
  hourly_rate: null,
  color_hex: null,
  display_order: 0,
  is_active: true,
};

const DEFAULT_COLOR_OPTIONS = [
  '#2f7a3c', '#1e6091', '#a45a52', '#6b4e7a', '#a08b1f',
  '#3a7d76', '#8b3a62', '#475569', '#b45309', '#0f766e',
];

export function AdminResourcesPanel({ token }: Props) {
  const [resources, setResources] = useState<AdminResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<FormMode>({ kind: 'closed' });
  const [form, setForm] = useState<ResourceInput>(emptyForm);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    api.adminResources(token)
      .then((d) => setResources(d.resources ?? []))
      .catch((err) => setError(err?.message || 'Could not load resources.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  function startNew() {
    setForm({ ...emptyForm });
    setMode({ kind: 'new' });
  }

  function startEdit(r: AdminResource) {
    setForm({
      resource_name: r.resource_name,
      resource_type: (r.resource_type as 'space' | 'staff' | 'equipment'),
      capacity: r.capacity ?? null,
      description: r.description ?? null,
      staff_user_id: r.staff_user_id ?? null,
      hourly_rate: r.hourly_rate ?? null,
      color_hex: r.color_hex ?? null,
      display_order: r.display_order ?? 0,
      is_active: r.is_active,
    });
    setMode({ kind: 'edit', resource: r });
  }

  function cancelForm() {
    setMode({ kind: 'closed' });
    setForm(emptyForm);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (!form.resource_name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (mode.kind === 'new') {
        await api.createResource(token, form);
      } else if (mode.kind === 'edit') {
        await api.updateResource(token, mode.resource.resource_id, form);
      }
      cancelForm();
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  async function archive(r: AdminResource) {
    if (!confirm(`Archive "${r.resource_name}"? Bookings stay intact; the resource just stops showing up in pickers.`)) return;
    try {
      await api.archiveResource(token, r.resource_id);
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not archive.');
    }
  }

  async function reactivate(r: AdminResource) {
    try {
      await api.updateResource(token, r.resource_id, { is_active: true });
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not re-activate.');
    }
  }

  const staffRows = resources.filter((r) => r.resource_type === 'staff');
  const spaceRows = resources.filter((r) => r.resource_type !== 'staff');

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resources</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Bookable rooms and staff. Assigning a staff member to an event creates a confirmed booking — the system will block conflicting assignments the same way it blocks double-booking a room.
          </p>
        </div>
        {mode.kind === 'closed' && (
          <button
            type="button"
            onClick={startNew}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
          >
            <Plus className="h-4 w-4" />
            Add resource
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {mode.kind !== 'closed' && (
        <form onSubmit={save} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-uif-green-dark">
              {mode.kind === 'new' ? 'New resource' : `Editing ${mode.resource.resource_name}`}
            </h2>
            <button
              type="button"
              onClick={cancelForm}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close form"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Name</span>
              <input
                type="text"
                required
                value={form.resource_name}
                onChange={(e) => setForm({ ...form, resource_name: e.target.value })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Type</span>
              <select
                value={form.resource_type ?? 'staff'}
                onChange={(e) => setForm({ ...form, resource_type: e.target.value as 'space' | 'staff' | 'equipment' })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              >
                <option value="staff">Staff (person)</option>
                <option value="space">Space (room/venue)</option>
                <option value="equipment">Equipment</option>
              </select>
            </label>

            {form.resource_type === 'space' && (
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Capacity</span>
                <input
                  type="number"
                  min={0}
                  value={form.capacity ?? ''}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value === '' ? null : Number(e.target.value) })}
                  disabled={saving}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
              </label>
            )}

            {form.resource_type === 'staff' && (
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Hourly rate (optional, for billing)</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.hourly_rate ?? ''}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value === '' ? null : Number(e.target.value) })}
                  disabled={saving}
                  placeholder="e.g. 45.00"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
              </label>
            )}

            <label className="block md:col-span-2">
              <span className="block text-xs font-medium text-gray-700 mb-1">Description</span>
              <input
                type="text"
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value || null })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Display order</span>
              <input
                type="number"
                min={0}
                value={form.display_order ?? 0}
                onChange={(e) => setForm({ ...form, display_order: e.target.value === '' ? 0 : Number(e.target.value) })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>

            <div className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Color (calendar tint)</span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, color_hex: null })}
                  className={
                    'h-7 px-2 rounded border text-[11px] ' +
                    (!form.color_hex ? 'border-uif-green bg-uif-green/10 text-uif-green-dark' : 'border-gray-300 text-gray-500 hover:bg-gray-50')
                  }
                >
                  Default
                </button>
                {DEFAULT_COLOR_OPTIONS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setForm({ ...form, color_hex: c })}
                    aria-label={`Pick ${c}`}
                    style={{ backgroundColor: c }}
                    className={
                      'h-6 w-6 rounded-full border-2 ' +
                      (form.color_hex === c ? 'border-gray-900' : 'border-white shadow')
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active ?? true}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                disabled={saving}
              />
              Active
            </label>
            <div className="flex-1" />
            <button
              type="button"
              onClick={cancelForm}
              disabled={saving}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.resource_name.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          </div>
        </form>
      )}

      <Section title="Staff" rows={staffRows} loading={loading} onEdit={startEdit} onArchive={archive} onReactivate={reactivate} />
      <Section title="Rooms &amp; spaces" rows={spaceRows} loading={loading} onEdit={startEdit} onArchive={archive} onReactivate={reactivate} />
    </div>
  );
}

interface SectionProps {
  title: string;
  rows: AdminResource[];
  loading: boolean;
  onEdit: (r: AdminResource) => void;
  onArchive: (r: AdminResource) => void;
  onReactivate: (r: AdminResource) => void;
}

function Section({ title, rows, loading, onEdit, onArchive, onReactivate }: SectionProps) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">{title}</h2>
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {loading && (
          <div className="p-4 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-4 text-sm text-gray-500 italic">No {title.toLowerCase()} yet.</div>
        )}
        {rows.map((r) => (
          <div key={r.resource_id} className="flex items-center gap-3 px-3 py-2">
            <span
              aria-hidden
              className="inline-block h-3 w-3 rounded-full border border-gray-300 flex-shrink-0"
              style={{ backgroundColor: r.color_hex || '#cbd5e1' }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={'text-sm font-medium ' + (r.is_active ? 'text-gray-900' : 'text-gray-400 line-through')}>
                  {r.resource_name}
                </span>
                {!r.is_active && <span className="text-[10px] uppercase tracking-wider text-gray-500">archived</span>}
                {r.staff_display_name && r.resource_type === 'staff' && (
                  <span className="text-xs text-gray-500">· {r.staff_email}</span>
                )}
                {r.capacity != null && r.resource_type !== 'staff' && (
                  <span className="text-xs text-gray-500">· cap {r.capacity}</span>
                )}
                {r.hourly_rate != null && r.resource_type === 'staff' && (
                  <span className="text-xs text-gray-500">· ${Number(r.hourly_rate).toFixed(2)}/hr</span>
                )}
              </div>
              {r.description && (
                <div className="text-xs text-gray-500 truncate">{r.description}</div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onEdit(r)}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                title="Edit"
                aria-label={`Edit ${r.resource_name}`}
              >
                <Edit2 className="h-4 w-4" />
              </button>
              {r.is_active ? (
                <button
                  type="button"
                  onClick={() => onArchive(r)}
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                  title="Archive"
                  aria-label={`Archive ${r.resource_name}`}
                >
                  <Archive className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onReactivate(r)}
                  className="p-1.5 rounded hover:bg-gray-100 text-uif-green-dark"
                  title="Re-activate"
                  aria-label={`Re-activate ${r.resource_name}`}
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
